import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, Container, Paper, useMediaQuery, useTheme } from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useMutation } from "@tanstack/react-query";

import {
    AuthLibMSALTokenAcquisitionError,
    useMSALPayload
} from "@initia-digital/auth-client-utils";
import { baseDefinedAndNotNullNarrowing, BasicLogger } from "@initia-digital/ts-utils";
import { TextButton, useSnackbar } from "@initia-digital/component-library";
import { IPrestart } from "@initia-digital/types/work-and-safety";

import { useWorkAndSafetyStore } from "../../../../../store";
import { postPrestart } from "../../../../../api/mutate";
import { BasicAppBarPage } from "../../../../../layouts/BasicAppBarPage";
import { EmergencyDirectiveBanner } from "../../../../../legacy/EmergencyDirectiveBanner";
import { PrestartHeader } from "../../../../../legacy/PrestartHeader";
import { CONFIG } from "../../../../../config/const";
import { PrestartStart } from "../../../../../legacy/PrestartStart";
import { ReviewThePlans } from "../../../../../legacy/ReviewThePlans";
import { HazardObservations } from "../../../../../legacy/HazardObservations";
import { ClosureAndContacts } from "../../../../../legacy/ClosureAndContacts";
import { WorkingWithOthers } from "../../../../../legacy/WorkingWithOthers";
import { PrestartActions } from "../../../../../legacy/PrestartActions";

const { PRESTART_STEPS } = CONFIG;

const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' });


const CreatePrestartDetail = () => {
    // -------------------------------------------------
    const {
        editPrestartState, resetEditPrestartState
    } = useWorkAndSafetyStore();
    const {
        id, status, leadingTeam
    } = editPrestartState;

    const navigate = useNavigate();
    const { jobId, planId } = useParams();

    const [searchParams] = useSearchParams();
    const stage = searchParams.get('stage') ?? PRESTART_STEPS[0].value;
    let activeStepIndex = PRESTART_STEPS.findIndex(step => step.value === stage);
    activeStepIndex = activeStepIndex !== -1 ? activeStepIndex : 0;

    // MUI HOOKS
    // ---------------------
    const theme = useTheme();
    const isSmallerThanMd = useMediaQuery(theme.breakpoints.down('md'));
    const { queueSnackbar } = useSnackbar();

    // AUTH AND DATA INTEGRATION
    // ---------------------
    const authDetail = useMSALPayload();
    // MUTATIONS
    // ---------------------
    const createPrestart = useMutation({
        mutationFn: postPrestart,
        onSuccess: (_, variables) => {
            // ---------------------
            resetEditPrestartState(jobId!, planId!);
            queueSnackbar(`Prestart created for ${variables.prestart.jobId}`, 'success', 'Success', 3000, '1rem');
        },
        onError: (error) => {
            // ----------------------------
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                // --------------------------------------------
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error saving prestart', { error });
            }
            logger.error('Error saving prestart');
            queueSnackbar('Prestart could not be created', 'error', 'Error saving prestart', 3000, '1rem');
        }
    });


    // Reset state when job changes
    // ---------------------
    useEffect(() => {
        if (baseDefinedAndNotNullNarrowing(jobId) && baseDefinedAndNotNullNarrowing(planId)) {
            // Only reset if the current state is for a different job and plan
            if (
                editPrestartState.jobId !== jobId || editPrestartState.planId !== planId
                || editPrestartState.id !== ''
            ) {
                // ---------------------
                resetEditPrestartState(jobId!, planId!);
            }
        }
    }, [jobId, editPrestartState.jobId, planId, editPrestartState.planId, editPrestartState.id, resetEditPrestartState]);

    // EVENT HANDLERS
    // ---------------------
    const handleSave = async (directUpdate?: IPrestart) => {
        // ----------------------------------------------

        if (!baseDefinedAndNotNullNarrowing(jobId)) {
            throw new Error('Job ID could not be picked up');
        }
        if (!baseDefinedAndNotNullNarrowing(planId)) {
            throw new Error('Plan ID could not be picked up');
        }

        // DEFAULT behaviour is to use the editPlanState
        let prestartCreate = editPrestartState;
        if (baseDefinedAndNotNullNarrowing(directUpdate)) {
            // -------------------------------------
            prestartCreate = directUpdate;
        }

        const res = await createPrestart.mutateAsync({
            prestart: {
                ...prestartCreate,
                jobId,
                planId
            },
            authDetail
        });

        navigate(`/job/${jobId}/plan/${planId}/edit-prestart/${res.id}?stage=${PRESTART_STEPS[activeStepIndex]!.value}`);
    }


    return (
        <BasicAppBarPage bgColor={"white"}>
            <Container maxWidth="md" sx={{ px: !isSmallerThanMd ? "25px" : "0px", py: "10px" }}>
                <EmergencyDirectiveBanner />
                <Box mx="10px" my="5px" pb="10px" height="100%">
                    <Paper sx={{ height: '100%', width: '100%' }} elevation={0}>
                        <Box
                            display="flex" flexDirection="row" justifyContent="center"
                            pt={{ xs: '10px', sm: '15px', md: '20px' }}
                            px={{ xs: '5px', md: '20px' }}
                            height="100%"
                        >
                            <Box display="flex" flexDirection="column" sx={{ width: '100%' }} >
                                <Box display="flex" flexDirection="column">
                                    <Box>
                                        <Box display="flex" mb="10px">
                                            <TextButton
                                                color={theme.palette.primary.contrastText}
                                                hoverBackgroundColor={theme.palette.primary.main}
                                                onClick={() => {
                                                    navigate(`/job/${jobId}`)
                                                }}
                                                startIcon={<ArrowBackIcon />}
                                            >
                                                {`Back to ${jobId}`}
                                            </TextButton>
                                        </Box>
                                    </Box>
                                    <PrestartHeader
                                        id={id}
                                        status={status}
                                        leadingTeam={leadingTeam}
                                    />
                                </Box>
                                <Box
                                    display="flex" flexDirection="column"
                                    mt="10px"
                                >
                                    <PrestartStart />
                                </Box>
                                <Box
                                    display="flex" flexDirection="column"
                                    mt="10px"
                                >
                                    <ReviewThePlans
                                        handleSave={handleSave}
                                    />
                                </Box>
                                <Box
                                    display="flex" flexDirection="column"
                                    mt="10px"
                                >
                                    <HazardObservations
                                        handleSave={handleSave}
                                    />
                                </Box>
                                <Box
                                    display="flex" flexDirection="column"
                                    mt="10px"
                                >
                                    <WorkingWithOthers />
                                </Box>
                                <Box
                                    display="flex" flexDirection="column"
                                    mt="10px"
                                >
                                    <ClosureAndContacts
                                        handleSave={handleSave}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
                <PrestartActions
                    handleSave={handleSave}
                />
            </Container>
        </BasicAppBarPage>
    )
}

export default CreatePrestartDetail;